import BaseObject from './BaseObject'

class Extract extends BaseObject {
  static _primaryKey = 'extract_id'
  static _apiBaseRoute = 'extract'
  static _objectType = 'extract'
  static _isExportable = true
  static _propConfigs = [
    {
      property_name: 'extract_id'
    },
    {
      property_name: 'name',
      validate: 'required'
    },
    {
      property_name: 'additional_info'
    },
    {
      property_name: 'abbreviation'
    },
    {
      property_name: 'clinic_part_number'
    },
    {
      property_name: 'dilution',
      mutator: this._cleanDilution,
      data_type: 'integer',
      validate: ['required', 'integer']
    },
    {
      property_name: 'percent_glycerin',
      validate: 'required',
      default: 0
    },
    {
      property_name: 'is_diluent',
      data_type: 'boolean',
      validate: 'required'
    },
    {
      property_name: 'test_order',
      data_type: 'integer',
    },
    {
      property_name: 'dilution_units',
      relation_class: null,
      relation_class_loader: async function () {
        return await import('./Unit')
      },
      relation_type: BaseObject._relationTypes.hasOne,
      validate: 'required'
    },
    {
      property_name: 'compatibility_class',
      relation_class: null,
      relation_class_loader: async function () {
        return await import('./CompatibilityClass')
      },
      relation_type: BaseObject._relationTypes.hasOne,
      validate: 'required'
    },
    {
      property_name: 'products',
      relation_class: null,
      relation_class_loader: async function () {
        return await import('./Product')
      },
      relation_type: BaseObject._relationTypes.hasMany,
      validate: 'required'
    }
  ]
  constructor(row = {}, prefix = '', hasManyPostfix = null) {
    super(row)
    this.prefix = prefix
    this.hasManyPostfix = hasManyPostfix
    if (Object.keys(row).length > 0) {
      this.constructor._propConfigs.forEach(propName =>
        this._addPropIfExists(propName, row)
      )
    }
  }
  isEquivalent = extract => {
    return (
      this.name != null &&
      extract.name != null &&
      this.name.toLowerCase() === extract.name.toLowerCase() &&
      this.dilution != null &&
      extract.dilution != null &&
      this.dilution == extract.dilution &&
      this.percent_glycerin != null &&
      extract.percent_glycerin != null &&
      parseFloat(this.percent_glycerin).toFixed(2) === extract.percent_glycerin
    )
  }
  buildImportableItem = () => {
    const importable = this._clean()
    // make compatibility_class relation
    if(this.compatibility_class) {
      importable.compatibility_class = this.compatibility_class.importableItem
    }

    delete importable.product //product will be a parent of extract rather than a child
    // make unit relation
    if(this.dilution_units) {
      importable.dilution_units = this.dilution_units.importableItem
    }
    delete importable.unit

    return importable
  }
  _cleanDilution = dilution => {
    return dilution.replace('1:', '').replace(',', '')
  }
  apiCreate = async callbacks => {
    // extracts aren't actually created, products with extracts relations are so really
    // we are making a product create call for each associated product (which will
    // contain this extract)
    // the first product create call will also create a new extract via its relation ship, 
    // we then need to update each additional product with the full extract details so that any additional
    // product create calls are linked to the initial extract rather than creating new ones.
    let parentExtract
    let firstProduct
    let remainingProducts
    if(Array.isArray(this.product) && this.product.length > 1) {
      firstProduct = this.product[0]
      remainingProducts = this.product.slice(1)
    } else if (Array.isArray(this.product)) {
      firstProduct = this.product[0]
    } else {
      firstProduct = this.product
    }

    await new Promise((resolve, reject) => {
      const internalCallbacks = {
        200: response => {
          firstProduct.updatePropsFromApi(response) // updates mapped id
          const product = {...response.data.data.product}
          parentExtract = {...response.data.data.product.extract}
          delete product.extract
          parentExtract.product = [product]
          resolve()
        }
      }
      // this is calling the product api_create which will create it and its
      // associated extract if needed
      // the product needs an extract child during creation
      firstProduct.apiCreate(internalCallbacks)
    })

    if(remainingProducts) {
      await this.asyncForEach(remainingProducts, async itemToCreate => {
        // apply the previously created extract object to this product for proper linking
        itemToCreate.extract.extract_id = parentExtract.extract_id
        itemToCreate.extract.mapped_id = parentExtract.extract_id
        await new Promise((resolve, reject) => {
          const internalCallbacks = {
            200: response => {
              itemToCreate.updatePropsFromApi(response) // updates mapped id
              // add this product to the parentExtract product array.
              const product = response.data.data.product
              delete product.extract
              parentExtract.product.push(product)
              resolve()
            }
          }
          // this is calling the product api_create which will create it and its
          // associated extract if needed
          // the product needs an extract child during creation
          itemToCreate.apiCreate(internalCallbacks)
        })
      })
    }
    if (callbacks[200] != null) {
      // finalResponse is a product creation response so we need to fake it into an
      // extract creation response.
      const extractResponse = {
        data: {
          data: {
            extract: parentExtract
          }
        }
      }
      callbacks[200](extractResponse)
    }
  }
}

export default Extract
